import React, { useState, useEffect, useCallback } from 'react';
import { PromptModalProps, PromptResponse } from '../types/prompts';
import { fetchPrompt, updatePrompt } from '../utils/api';
import VersionHistoryModal from './VersionHistoryModal';

const PromptDocsModal: React.FC<PromptModalProps> = ({ isOpen, onClose, promptName }) => {
  const [promptData, setPromptData] = useState<PromptResponse | null>(null);
  const [promptContent, setPromptContent] = useState<string>('');
  const [originalContent, setOriginalContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  const loadPromptData = useCallback(async () => {
    if (!promptName) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const promptResponse = await fetchPrompt(promptName);
      setPromptData(promptResponse);
      setPromptContent(promptResponse.content);
      setOriginalContent(promptResponse.content);
    } catch (err) {
      setError('Failed to load prompt data');
      console.error('Error loading prompt data:', err);
    } finally {
      setLoading(false);
    }
  }, [promptName]);

  useEffect(() => {
    if (isOpen && promptName) {
      loadPromptData();
    } else {
      setIsEditing(false);
      setError(null);
      setSuccessMessage(null);
    }
  }, [isOpen, promptName, loadPromptData]);

  const handleSave = async () => {
    if (!promptName || promptContent === originalContent) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await updatePrompt(promptName, promptContent);
      setSuccessMessage(`✅ ${result.message}`);
      setOriginalContent(promptContent);
      setIsEditing(false);
      await loadPromptData();
    } catch (err) {
      setError('Failed to save prompt');
      console.error('Error saving prompt:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setPromptContent(originalContent);
    setIsEditing(false);
    setSuccessMessage(null);
    setError(null);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      if (isEditing && promptContent !== originalContent) {
        if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
          handleCancel();
          onClose();
        }
      } else {
        onClose();
      }
    }
  };

  const hasChanges = promptContent !== originalContent;

  if (!isOpen) return null;

  return (
    <div className="prompt-modal-backdrop" onClick={handleBackdropClick}>
      <div className="prompt-modal">
        <div className="prompt-modal-header">
          <div className="prompt-header-info">
            <h2>{promptName}.md</h2>
            {promptData?.metadata && (
              <div className="prompt-metadata">
                <span className="version-badge">{promptData.metadata.version}</span>
                <span className="last-updated">
                  Updated: {new Date(promptData.metadata.last_updated).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
          <button className="modal-close-btn" onClick={() => {
            if (isEditing && hasChanges) {
              if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
                handleCancel();
                onClose();
              }
            } else {
              onClose();
            }
          }}>
            ✕
          </button>
        </div>

        <div className="prompt-modal-content">
          {loading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading prompt data...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p>❌ {error}</p>
              <button onClick={loadPromptData} className="retry-btn">
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && promptData && (
            <>
              {successMessage && (
                <div className="success-state">
                  <p>{successMessage}</p>
                </div>
              )}

              <div className="modal-button-bar">
                <div className="section-actions">
                  {!isEditing ? (
                    <>
                      <button 
                        onClick={() => setIsEditing(true)}
                        className="edit-btn"
                        title="Edit prompt"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => setShowVersionHistory(true)}
                        className="version-history-btn"
                        title="View Version History"
                      >
                        Version History
                      </button>
                    </>
                  ) : (
                    <>
                      <button 
                        onClick={handleCancel}
                        className="cancel-btn"
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button 
                        onClick={handleSave}
                        className="save-btn"
                        disabled={saving || !hasChanges}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="prompt-content-section">
                <div className="section-header">
                  <h3>Prompt Content</h3>
                </div>

                {isEditing ? (
                  <textarea
                    value={promptContent}
                    onChange={(e) => setPromptContent(e.target.value)}
                    className="prompt-editor"
                    rows={14}
                    placeholder="Enter your prompt content here..."
                  />
                ) : (
                  <div className="prompt-display">
                    <pre className="prompt-text">{promptContent}</pre>
                  </div>
                )}

                {hasChanges && (
                  <div className="changes-indicator">
                    ⚠️ You have unsaved changes
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <VersionHistoryModal
        isOpen={showVersionHistory}
        onClose={() => setShowVersionHistory(false)}
        promptName={promptName}
        onVersionRestored={async () => {
          setSuccessMessage('✅ Version restored successfully');
          await loadPromptData();
        }}
      />
    </div>
  );
};

export default PromptDocsModal;
